import {
  APIAction,
  IndirectAPIData,
  IndirectAPIRequestStoreFunction,
  APIActionGenerator,
  emptyResults,
  storeResults,
  APIData,
} from '../apidata';
import { PaginationOptions, PaginatedAPIRequest } from './common';

export type MapIndirectOptions<P> = {
  store: IndirectAPIRequestStoreFunction<any, P, any>;
  getKeyByEntry: (entry: P) => string | undefined;
};

export type MapIndirectPaginationAction<T> = APIAction<any, T>;
export type MapIndirectIndirectAction<P> = APIAction<any, P>;
export type MapIndirectAction<T, P> =
  | MapIndirectPaginationAction<T>
  | MapIndirectIndirectAction<P>;

export type MapIndirectPaginationFunction<T = unknown, P = unknown> = (
  state: IndirectAPIData<P, any>,
  action: MapIndirectAction<T, P>
) => IndirectAPIData<P, any>;

export function paginationMapIndirect<
  R extends PaginatedAPIRequest<T, E>,
  T = unknown,
  P = unknown,
  E = unknown
>(
  opts: PaginationOptions<R, T, P, E>,
  generator: APIActionGenerator<R, T, E>,
  mapOpts: MapIndirectOptions<P>,
  state: IndirectAPIData<P, any>,
  action: MapIndirectAction<T, P>
): IndirectAPIData<P, any> {
  const { types } = generator;
  const { translatePage } = opts;
  const { store, getKeyByEntry } = mapOpts;
  switch (action.type) {
    case types.success: {
      const paginatedAction = action as MapIndirectPaginationAction<T>;
      const { response } = paginatedAction;
      if (!response || !response.success) {
        return state;
      }

      // Populate the singleton data from the page data.
      const newEntries: IndirectAPIData<P, any> = {};
      const translatedPage = translatePage(response.data);
      for (const newEntryData of translatedPage.results) {
        const key = getKeyByEntry(newEntryData);

        // Since the getKeyByEntry function can return undefined, go ahead and
        // just continue processing the results in that case as it wouldn't fit
        // in the Record type anyway.
        if (key === undefined) {
          continue;
        }

        const entry = state[key] || emptyResults;
        const entryData = entry.isFilled && entry.success ? entry.data : {};
        newEntries[key] = storeResults({
          // Default values needed by APIResult
          url: '',
          status: 200,

          // Legacy entry data
          ...entry,

          // List data
          data: {
            // Old data (in case the paginated data has incomplete info)
            ...entryData,

            // New data from pagination endpoint
            ...newEntryData,
          },

          success: true,
        });
      }

      return {
        ...state,
        ...newEntries,
      };
    }

    default:
      return store(state, action as MapIndirectIndirectAction<P>);
  }
}

/**
 * Convert given list of entries to a list of mapped entries wrapped in an
 * APIData type.
 *
 * @param state - state of the indirect store
 * @param getKeyByEntry - function converting an entry to its key in the
 *   indirect store
 * @param entries - list of entries from paginated data
 */
export function listMapEntries<P, E>(
  state: IndirectAPIData<P, E>,
  getKeyByEntry: (entry: P) => string | undefined,
  entries: P[]
): APIData<P, E>[] {
  return entries.map((entry: P) => {
    const key = getKeyByEntry(entry);
    if (key === undefined) {
      return emptyResults;
    }
    return state[key] ?? emptyResults;
  });
}

/**
 * Convert given list of entries to a list of mapped entries, skipping all that
 * aren't found or for whom the fetch action failed.
 *
 * @param state - state of the indirect store
 * @param getKeyByEntry - function converting an entry to its key in the
 *   indirect store
 * @param entries - list of entries from paginated data
 */
export function listMapEntryData<P>(
  state: IndirectAPIData<P, any>,
  getKeyByEntry: (entry: P) => string | undefined,
  entries: P[]
): P[] {
  const ret: P[] = [];
  for (const entry of listMapEntries(state, getKeyByEntry, entries)) {
    if (!entry.isFilled || !entry.success) {
      continue;
    }
    ret.push(entry.data);
  }
  return ret;
}
